import { useState, useCallback } from 'react';
import { functions } from '../firebase/firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { firebaseCache } from '../utils/firebaseCache';

/**
 * useAdminAuth Hook
 * 
 * Provides admin-specific authentication operations:
 * - linkStaffToAdmin: Link a staff member to the current admin
 * - listStaffsForAdmin: Get all staff linked to the current admin
 * - deleteStaffAccount: Permanently delete a staff member and all their data
 * 
 * Usage:
 *   const { linkStaff, listStaffs, deleteStaff, loading, error } = useAdminAuth();
 *   
 *   // Link staff by email
 *   await linkStaff({ staffEmail: 'staff@example.com' });
 *   
 *   // List all linked staff
 *   const staffs = await listStaffs();
 */
export const useAdminAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Link a staff member to the current admin
   * @param {Object} params - { staffUid?: string, staffEmail?: string }
   * @returns {Promise<Object>} { success, staffUid, message }
   */
  const linkStaff = useCallback(async (params) => {
    setLoading(true);
    setError(null);

    try {
      const linkStaffToAdmin = httpsCallable(functions, 'linkStaffToAdmin');
      // Pass apiKey for server-side password verification
      const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
      const result = await linkStaffToAdmin({
        ...params,
        apiKey
      });

      // Invalidate staff cache so the list is refreshed
      firebaseCache.markForInvalidation('staffs');

      return result.data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to link staff member';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get all staff members linked to the current admin
   * @returns {Promise<Array>} Array of staff objects
   */
  const listStaffs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const listStaffsForAdmin = httpsCallable(functions, 'listStaffsForAdmin');
      const result = await listStaffsForAdmin({});
      return result.data.staffs || [];
    } catch (err) {
      const errorMessage = err.message || 'Failed to list staff members';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Permanently delete a staff member and all their data
   * @param {string} staffUid - UID of staff to delete
   * @returns {Promise<Object>} { success, message, deletedCounts }
   */
  const deleteStaff = useCallback(async (staffUid) => {
    setLoading(true);
    setError(null);

    try {
      const deleteStaffAccount = httpsCallable(functions, 'deleteStaffAccount');
      const result = await deleteStaffAccount({ staffUid });

      // Invalidate staff cache so the list is refreshed
      firebaseCache.markForInvalidation('staffs');

      return result.data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete staff member';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    linkStaff,
    listStaffs,
    deleteStaff,
    loading,
    error,
  };
};

export default useAdminAuth;
